<template>
  <div id="file-preview-wrapper">
    <request-new-version ref="requestNewVersion" :record="record" :userVersionOptions="currentUserVersionOptions"/>
    <upload-new-version-link ref="uploadNewVersionLink" :record="record"
                             :userVersionOptions="currentUserVersionOptions"/>
    <share-record-form ref='shareRecordForm' @success='loadRecord(); fetchTabData()'/>
    <record-subscription-modal ref='recordSubscriptionModal' @reload-callback='loadRecord(); fetchTabData()'
                               @success='loadRecord(); fetchTabData()'/>

    <modal ref="selectFromRelaticoFolder">
      <template #header>
        <h5 class="modal-title mt-0">{{ $t('upload_new_version_link.choose_file', {folderName: folder.name}) }}</h5>
      </template>
      <template #body>
        <form class="p-b-20">
          <div class="modal-body">
            <div class="form-group">
              <version-options-form :userVersionOptions="currentUserVersionOptions"
                                    @version-options-callback="versionOptionsFormCallback"/>
              <label>{{ $t('record_show.choose_folder') }}</label>
              <single-select :options='recordsForNewVersion' v-model="recordForVersion" ref="recordsForNewVersion"/>
            </div>
            <div class='float-right'>
              <button type="submit" @click.prevent='makeRecordCurrent' :disabled="isProcessing"
                      class="btn btn-outline-primary">
                {{ $t('record_show.choose_button') }}
                <i class="fas fa-circle-notch fa-spin" v-show="isProcessing"></i>
              </button>
            </div>
          </div>

        </form>
      </template>
    </modal>

    <modal ref='uploadVersionModal'>
      <template #header>
        <h5 class="modal-title mt-0">{{ $t('upload_new_version.title') }}</h5>
      </template>

      <template #body>
        <div class='modal-body'>
          <version-options-form :userVersionOptions="currentUserVersionOptions"
                                @version-options-callback="versionOptionsFormCallback"/>
          <dropzone ref='dropzone' :uppyParams='{ restrictions: { maxNumberOfFiles: 1 } }'
                    :successCallback='uppySuccessCallback' :completeCallback='uppyCompleteCallback'></dropzone>
        </div>
      </template>
    </modal>

    <modal ref='editNoteModal'>
      <template #header>
        <h5 class="modal-title mt-0">{{ $t('notes.edit_note') }}</h5>
      </template>

      <template #body>
        <form @submit.prevent='editNote($event)'>
          <div class='modal-body'>
            <label>{{ $t('notes.edit_note') }}</label>
            <input type='text' name='note[body]' v-model='modalNote.body' class="form-control"/>
          </div>

          <div class='modal-footer'>
            <input type='submit' :value="$t('notes.save')" class='btn btn-primary'/>
          </div>
        </form>
      </template>
    </modal>

    <modal ref='deleteNoteModal'>
      <template #header>
        <h5 class="modal-title mt-0">{{ $t('notes.delete_note') }}</h5>
      </template>

      <template #body>
        <form @submit.prevent='deleteNote'>
          <div class='modal-body'>
            <p>{{ $t('notes.are_you_sure') }}</p>
          </div>

          <div class='modal-footer'>
            <input @click.prevent='$refs.deleteNoteModal.close' type='submit' :value="$t('notes.close')"
                   class="btn btn-outline-primary"/>
            <input type='submit' :value="$t('notes.delete')" class="btn btn-danger"/>
          </div>
        </form>
      </template>
    </modal>

    <modal ref='deleteRecordVersionModal'>
      <template #header>
        <h5 class="modal-title mt-0">{{ $t('record_show.delete_file') }}</h5>
      </template>

      <template #body>
        <form @submit.prevent='deleteVersion'>
          <div class='modal-body'>
            <p>{{ $t('record_show.delete_label') }}</p>
          </div>

          <div class='modal-footer'>
            <input @click.prevent='$refs.deleteRecordVersionModal.close' type='submit' :value="$t('folder_show.modals.delete.cancel')" class="btn btn-outline-primary">
            <input type='submit' :value="$t('folder_show.modals.delete.submit')" class='btn btn-danger'/>
          </div>
        </form>
      </template>
    </modal>

    <modal ref='deleteRecordModal'>
      <template #header>
        <h5 class="modal-title mt-0">{{ $t('record_show.delete_file') }}</h5>
      </template>

      <template #body>
        <form @submit.prevent='deleteFile'>
          <div class='modal-body'>
            <p>{{ $t('record_show.delete_label') }}</p>
          </div>

          <div class='modal-footer'>
            <input @click.prevent='$refs.deleteRecordModal.close' type='submit'
                   :value="$t('folder_show.modals.delete.cancel')" class="btn btn-outline-primary">
            <input type='submit' :value="$t('folder_show.modals.delete.submit')" class='btn btn-danger'/>
          </div>
        </form>
      </template>
    </modal>

    <modal ref='extractText'>
      <template #header>
        <h5 class="modal-title mt-0">{{ $t('record_show.extract_text_from_pdf') }}</h5>
      </template>

      <template #body>
        <form @submit.prevent='createOcrFromRecord'>
          <div class='modal-body extract-modal'>
            <p>{{ $t('record_show.you_can') }}</p>
            <ul>
              <li>{{ $t('record_show.search') }}</li>
              <li>{{ $t('record_show.extract') }}</li>
              <li>{{ $t('record_show.find') }}</li>
            </ul>
            <p>{{ $t('record_show.note') }}</p>
            <strong>
              {{ $t('record_show.file_count') }} {{ `${ocrFileCurrent} / ${ocrFileLimit}` }}
              {{ $t('record_show.processed') }}
            </strong>
          </div>
          <div class='modal-footer' v-if="record.ocr_in_progress">
            <strong>In progress</strong>
          </div>
          <div class='modal-footer' v-else-if="record.ocr_content">
            <strong>{{ $t('record_show.text_extracted') }}</strong>
          </div>
          <div class="modal-footer" v-else-if="ocrLimitReached">
            <p class="benefits-ocr">{{ $t('record_show.file_limit') }}
              <a href="mailto:christian.alsen@relatico.com">christian.alsen@relatico.com</a>
            </p>
          </div>
          <div class="modal-footer" v-else-if="!ocrLimitReached">
            <input @click.prevent='$refs.extractText.close' type='submit'
                   :value="$t('folder_show.modals.delete.cancel')" class="btn btn-neutral">
            <input type='submit' :value="$t('record_show.button_extract_text')" class='btn btn-primary'/>
          </div>
        </form>
      </template>
    </modal>

    <div class="file-preview-header">
      <div class="header-info">
        <p class="back-button" @click.prevent='returnToFolder'><i class="fas fa-arrow-left"></i>
          {{ $t('record_show.back_to_folder') }}</p>
        <div class="filename">
          <p>{{ record.filename }}</p>
          <a :href="fileActions()" target="_blank" class="own-tooltip own-tooltip-bottom video-link"
             :data-tooltip="$t('record_show.click_to_watch_all_actions')"><img src="../assets/video.svg"/></a>
          <a @click.prevent="starRecord()" href="#" class="starred">
            <i :class="['fa-star', { far: !record.starred, fas: record.starred }]"></i>
          </a>
          <a href="#" @click.prevent='$refs.recordSubscriptionModal.open(record)' v-show="isSubscribed"
             :data-tooltip="$t('record_show.subscribed')" class="own-tooltip own-tooltip-bottom">
            <img src="../assets/subscribed.svg"/>
          </a>
          <div class="exp-status">
            <span v-if="record.status == 'current'">{{ formatDate(record.expiration_date) }}</span>
            <span class="soon" v-else-if="record.status == 'soon'">{{ formatDate(record.expiration_date) }}</span>
            <span class="expired" v-else-if="record.status == 'expired'">{{ $t('records_list.table.expired') }}</span>
            <span class="no-expiration" v-else>{{ $t('records_list.table.no_expiration') }}</span>
          </div>
          <div class="uploaded-at">
            <strong>{{ $t('record.detail_tab.uploaded_at') }}</strong>
            {{ formatDate(record.created_at) }}
          </div>
          <p v-if="record.shared" class="file-is-shared">{{ $t('records_list.shared') }}
            <a @click.prevent='$refs.shareRecordForm.open(record)' href='#'>{{
                $t('records_list.show_shareable_link')
              }}</a>
          </p>
        </div>
        <div class="file-source my-2">
          <div>
            {{ $t('record.file_uploaded_through') }}
            <span>{{ uploadedFrom }}</span>
            <router-link v-if="documentResponse && record.uploaded_from === 'email'"
                         :to="{name: 'message', params: { id: documentResponse.id, referrer: 'inbox', messageType: 'inbox' }}">
              {{ $t('record_show.go_to_received_message') }}
            </router-link>
          </div>
        </div>
        <div class="folder-info">
          <img alt="Folder" src="../assets/folders-icon.svg" class="folder-icon">
          <router-link v-if='contact' :to="{name: 'contact_folders', params: { id: contact.id }}">
            {{ contact.name }}
          </router-link>
          <span v-if='contact'>
            &gt;
          </span>
          <router-link v-if='folder.primary' :to="{name: 'folders'}">
            {{ folderTemplate(folder) }}
          </router-link>
          <router-link v-else :to="{name: 'folder', params: { id: folder.id }}">
            {{ folderTemplate(folder) }}
          </router-link>
        </div>
        <div class="file-tags" v-if="recordTags.length > 0">
          <h4>{{ $t('record_show.tags_show') }}</h4>
          <div class='existing-tags-wrapper' :class='tag.color' v-for='tag in recordTags'>
            <div class='btn-group btn-group-toggle' data-toggle='buttons'>
              <label class='btn active'>
                <router-link :to="{name: 'tag', params: { id: tag.id }}">
                  {{ tag.name }}
                </router-link>
              </label>
              <label class='btn' @click.prevent='removeConnectedTag(tag)'>
                <i class='fa fa-remove'></i>
              </label>
            </div>
          </div>
        </div>
        <checklist-preview ref="checklistPreview" :checklist='checklist' v-click-outside="hideChecklistPreview" />
      </div>
      <div class="header-controls">
        <router-link v-if='prevRecord' :to="{ params: { id: prevRecord.id } }"
                     class="btn btn-neutral m-r-20 own-tooltip own-tooltip-bottom" :data-tooltip="prevRecord.name">
          <i class="fas fa-chevron-left"></i>
        </router-link>

        <router-link v-if='nextRecord' :to="{ params: { id: nextRecord.id } }"
                     class="btn btn-neutral own-tooltip own-tooltip-bottom" :data-tooltip="nextRecord.name">
          <i class="fas fa-chevron-right"></i>
        </router-link>
      </div>
    </div>
    <div class="file-preview">
      <div class="file-preview-display">
        <div class="nav-tabs-wrapper">
          <ul class="nav nav-tabs" role="tablist">
            <li class="nav-item">
              <a class="nav-link active" id="orig-doc-tab" data-toggle="tab" href="#orig-doc" role="tab"
                 aria-controls="orig-doc" aria-selected="true">{{ $t('record_show.original_document') }}</a>
            </li>
            <li class="nav-item" v-if="isOcrEnabled">
              <a class="nav-link" id="extr-text-tab" data-toggle="tab" href="#extr-text" role="tab"
                 aria-controls="extr-text" aria-selected="false">{{ $t('record_show.extracted_text') }}</a>
            </li>
          </ul>
        </div>
        <div class="tab-content">
          <div class="tab-pane fade show active original-doc" id="orig-doc" role="tabpanel"
               aria-labelledby="orig-doc-tab">
            <img src="../assets/text-extracted.png" class="text-extracted-img" v-if="record.ocr_content"
                 :alt="$t('record_show.extracted_text')"/>
            <preview :record='record'></preview>
          </div>
          <div class="tab-pane fade extracted-text-tab" id="extr-text" role="tabpanel" aria-labelledby="extr-text-tab"
               v-if="isOcrEnabled">
            <div class="extracted-text" v-if="record.ocr_in_progress">
              {{ $t('record_show.extraction_in_progress') }}
            </div>
            <div class="unextracted-text" v-else-if="!record.ocr_content">
              <i class="fas fa-lock"></i>
              <div class="benefits-ocr" v-if="!ocrLimitReached">
                <p>{{ $t('record_show.find_content') }}</p>
                <p>{{ $t('record_show.extract_needed_text') }}</p>
                <p>{{ $t('record_show.use_search') }}</p>
                <a href="#" @click.prevent='openModal($refs.extractText)'
                   class="btn btn-primary">{{ $t('record_show.extract_text_from_pdf') }}</a>
              </div>
              <p class="benefits-ocr" v-if="ocrLimitReached">{{ $t('record_show.file_limit') }}
                <a href="mailto:christian.alsen@relatico.com">christian.alsen@relatico.com</a>
              </p>
              <img src="../assets/file-show/file.svg"/>
            </div>
            <div class="extracted-text" v-else>
              <div class="search-wrapper">
                <input type="text" v-model="searchQuery" class="form-control"
                       :placeholder="$t('record_show.search_in_extraction')"/>
              </div>
              <ul>
                <li v-for="row in ocrFilteredContent">
                  <h2>Page ({{ row.page }})</h2>
                  <p v-html="$options.filters.makeItRed(row.text, searchQuery)"></p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <hr>
      <div class="file-actions" v-click-outside='hideIt'>
        <h3>{{ $t('record_show.add_to_file') }}</h3>
        <div class="operations">
          <a href="#" @click.prevent="showing = 'checklist'">
            <img alt="Checklist" src="../assets/file-show/checklist.svg"/>
            <span>{{ $t('record_show.checklist_items') }}</span>
          </a>
          <a :href="checklistActions()" target="_blank" class="own-tooltip own-tooltip-left video-link"
             :data-tooltip="$t('record_show.click_to_watch_checklist')"><img src="../assets/video.svg"/></a>
          <div class="checklist-items set-data" v-if="showing === 'checklist'">
            <i @click="hideIt" class="fas fa-times"></i>
            <p v-if='folder.primary' class="text-center no-checklist">
              {{ $t('record_show.checklist_on_primary_folder') }}
            </p>
            <checklist v-else-if="record.id" :record="record" :folder="record.folder" @success="loadRecord"/>
          </div>
        </div>
        <div class="operations">
          <a href="#" @click.prevent="showing = 'expiration'">
            <img alt="Expiration date" src="../assets/file-show/reminder.svg"/>
            <span>{{ $t('record_show.exp_date') }}</span>
          </a>
          <div class="set-data" v-if="showing === 'expiration'">
            <i class="fas fa-times" @click="hideIt"></i>
            <expiration-form :record='record' :contact='contact' :submitCallback='updateExpiration' :orgContactReminderDays='currentOrgContactReminderDays'/>
          </div>
        </div>
        <div class="operations">
          <router-link :to="{ hash: '#versions' }" @click.native='openVersions()'>
            <img alt="Versions date" src="../assets/file-show/versioning.svg"/>
            <span>{{ $t('record_show.file_versions') }}</span>
          </router-link>
          <a :href="fileVersionsActions()" target="_blank" class="own-tooltip own-tooltip-left video-link"
             :data-tooltip="$t('record_show.click_to_watch_file_versions')"><img src="../assets/video.svg"/></a>
          <div class="set-data versions" v-if="showing == 'versions'">
            <i class="fas fa-times" @click="hideIt"></i>
            <table class="table" v-if="recordVersions.length > 0">
              <tr>
                <th>{{ $t('versions.file_name') }}</th>
                <th>{{ $t('versions.created_at') }}</th>
                <th>{{ $t('versions.version') }}</th>
                <th>{{ $t('versions.actions') }}</th>
              </tr>
              <tbody name="fade" is="transition-group">
              <tr v-for='(recordVersioned, index) in recordVersions' :key='index'>
                <td v-if="recordVersioned.next_record_id == null">
                  {{ recordVersioned.filename }}
                </td>
                <td v-else>
                  <router-link
                      :to="{ name: 'record_show_versioned', params: { id: recordVersioned.id, record_id: record.id } }">
                    {{ recordVersioned.filename }}
                  </router-link>
                </td>
                <td>{{ formatDate(recordVersioned.created_at) }}</td>
                <td>
                  <strong class="version-no">
                    {{ `V${recordVersioned.version}` }}
                    <span v-if='recordVersioned.next_record_id == null'>{{ $t('versions.current') }}</span>
                  </strong>
                </td>
                <td class="actions">
                  <ul>
                    <li class="dropdown-item">
                      <a :href="previewUrl(recordVersioned.location)" :download='recordVersioned.filename' target="_blank">
                        <i class="fas fa-download"></i>
                      </a>
                    </li>
                    <li class='dropdown-item' v-if='recordVersioned.next_record_id'>
                      <a href="#" @click.prevent='openRecordVersionModal(recordVersioned, $refs.deleteRecordVersionModal)'>
                        <i class="fas fa-trash"></i>
                      </a>
                    </li>
                  </ul>
                </td>
              </tr>
              </tbody>
            </table>
            <div class="no-versions" v-else>
              <p>{{ $t('record_show.only_version') }}</p>
            </div>
            <div class="version-action-container">
              <div class="dropdown">
                <button aria-expanded="true" aria-haspopup="true" data-toggle="dropdown" type="button"
                        class="btn btn-outline-primary dropdown-toggle add-v">
                  {{ $t('upload_new_version_link.add_new_version') }}
                </button>
                <div class="dropdown-menu">
                  <a @click.prevent='$refs.uploadVersionModal.open' href='#' class="dropdown-item">
                    <div class="fa fa-desktop"></div>
                    {{ $t('upload_new_version_link.upload_from_computer') }}
                  </a>
                  <a @click.prevent='openModalForRecords($refs.selectFromRelaticoFolder)' href='#'
                     class="dropdown-item">
                    <div class="fa fa-folder-open"></div>
                    {{ $t('upload_new_version_link.choose_from_folder') }}
                  </a>
                </div>
              </div>
              <div class="dropdown">
                <button aria-expanded="true" aria-haspopup="true" data-toggle="dropdown" type="button"
                        class="btn btn-outline-primary dropdown-toggle">
                  {{ $t('upload_new_version_link.request_new_version') }}
                </button>

                <div class="dropdown-menu">
                  <a @click.prevent="$refs.requestNewVersion.openModal()" href="#" class="dropdown-item">
                    <div class="fa fa-envelope-o"></div>
                    {{ $t('versions.request_email') }}
                  </a>
                  <a @click.prevent="$refs.uploadNewVersionLink.openModal()" href="#" class="dropdown-item">
                    <div class="fa fa-chain"></div>
                    {{ $t('versions.request_link') }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="operations">
          <router-link :to="{ hash: '#tags' }" @click.native=openTags()>
            <img alt="Tags" src="../assets/file-show/tag.svg"/>
            <span>{{ $t('record_show.tags') }}</span>
          </router-link>
          <div class="set-data tags-data" v-if="showing === 'tags'">
            <i class="fas fa-times" @click="hideIt"></i>
            <tags :record='record' @reload-record='loadRecord()'/>
          </div>
        </div>
        <div class="operations">
          <router-link :to="{ hash: '#notes' }" @click.native=openNotes()>
            <img alt="Notes" src="../assets/file-show/notes.svg"/>
            <span>{{ $t('record_show.notes') }}</span>
            <span class="notes-count" v-if="recordNotes.length > 0">{{ recordNotes.length }}</span>
          </router-link>
          <div class="set-data" v-if="showing === 'notes'">
            <i class="fas fa-times" @click="hideIt"></i>
            <ul v-if='recordNotes' class="existing_notes">
              <li v-for='note in recordNotes' :key='note.id'>
                <user-image :user='note.creator'/>
                <div class="note-details-container">
                  <p class="date">
                    {{ formatDate(note.created_at) }}
                  </p>
                  <p class="text">
                    {{ note.body }}
                  </p>
                </div>
                <div class="note-actions-container">
                  <a @click.prevent='openNoteModal(note, $refs.editNoteModal)' href="#">
                    <i class="fas fa-edit"></i>
                  </a>

                  <a @click.prevent='openNoteModal(note, $refs.deleteNoteModal)' href="#">
                    <i class="fas fa-trash"></i>
                  </a>
                </div>
              </li>
            </ul>
            <form @submit.prevent='createNote'>
              <label for="note_body"></label>
              <textarea id='note_body' name="note[body]" :placeholder="$t('notes.place_holder')"
                        rows="10" cols="40" class="note_body tinymce"></textarea>

              <button type="submit" class="btn btn-outline-primary submit-note float-right">
                {{ $t('notes.submit_note') }}
              </button>
            </form>
          </div>
        </div>
        <div class="operations" v-if="toggles('file_communication')">
          <router-link :to="{ hash: '#messages' }" @click.native="openRecordMessages()">
            <img src="../assets/file-show/envelope.svg" />
            <span class="messages-count" v-show="repliesStats.length > 0 || newMessageStats.length > 0">{{ repliesStats.length + newMessageStats.length }}</span>
            <span>{{ $t('record_show.communication') }}</span>
          </router-link>
          <communication ref="communication" v-show="showing === 'messages'"
            :currentRecord="record"
            :hash="$props.hash"
            @success='loadStatsRecordMessage()'
          />
        </div>
        <h3>{{ $t('record_show.actions') }}</h3>
        <div class="operations">
          <a href='#' @click="showing = 'rename'">
            <img alt="Rename" src="../assets/file-show/rename.svg"/>
            <span>{{ $t('record_show.rename') }}</span>
          </a>
          <div class="set-data" v-if="showing == 'rename'">
            <i class="fas fa-times" @click="hideIt"></i>
            <form @submit.prevent='renameFile($event)' class="modal-fix">
              <label>{{ $t('records_list.file_name') }}</label>
              <input type='text' name='record[filename]' class='form-control' v-model='modalRecord.filename'/>
              <input type='submit' :value="$t('records_list.dropdown.rename')" class='btn btn-primary float-right'/>
            </form>
          </div>
        </div>
        <div class="operations">
          <a href='#' @click.prevent='$refs.recordSubscriptionModal.open(record)'>
            <img :alt="$t('folder_show.activities.subscribe_to_record')" src="../assets/actions-subscribed.svg"/>
            <span v-if='isSubscribed'>
              {{ $t('folder_show.activities.unsubscribe_from_record') }}
            </span>
            <span v-else>
              {{ $t('folder_show.activities.subscribe_to_record') }}
            </span>
          </a>
        </div>
        <div class="operations">
          <a :href="previewUrl(record.location)" :download='record.filename' target="_blank">
            <img alt="Download" src="../assets/file-show/download.svg"/>
            <span>{{ $t('record_show.download') }}</span>
          </a>
        </div>
        <div class="operations" v-if="isOcrEnabled">
          <a href='#' @click.prevent='openModal($refs.extractText)'>
            <img alt="Extract text" src="../assets/file-show/extract.svg"/>
            <span>{{ $t('record_show.extract_text') }}</span>
          </a>
          <a :href="textExtractActions()" target="_blank" class="own-tooltip own-tooltip-left video-link"
             :data-tooltip="$t('record_show.click_to_watch_text_extraction')"><img src="../assets/video.svg"/></a>
        </div>
        <div class="operations">
          <router-link :to="{ hash: '#copy' }" @click.native="showing = 'copy'">
            <img alt="Copy" src="../assets/file-show/copytofile.svg"/>
            <span>{{ $t('record_show.copy_folder') }}</span>
          </router-link>
          <div class="set-data" v-if="showing == 'copy'">
            <i class="fas fa-times" @click="hideIt"></i>
            <form @submit.prevent='copyFile($event)' class="modal-fix">
              <label>{{ $t('record_show.folder') }}</label>
              <infinite-single-select :template='folderTemplate' :getter='getFolders' name='record[folder_id]' v-model='folderToCopy'/>
              <input type='submit' :value="$t('folder_show.modals.copy.submit')" class='btn btn-primary float-right'/>
            </form>
          </div>
        </div>
        <div class="operations">
          <router-link :to="{ hash: '#move' }" @click.native="showing = 'move'">
            <img alt="Move" src="../assets/file-show/folders.svg"/>
            <span>{{ $t('record_show.move_folder') }}</span>
          </router-link>
          <div class="set-data" v-if="showing == 'move'">
            <i class="fas fa-times" @click="hideIt"></i>
            <form @submit.prevent='moveFile($event)' class="modal-fix">
              <label>{{ $t('record_show.folder') }}</label>
              <infinite-single-select :template='folderTemplate' :getter='getFolders' name='record[folder_id]' v-model='folderToMove'/>
              <input type='hidden' name='action_name' value="move"/>
              <input type='submit' :disabled="moveActionDisabled" :value="$t('folder_show.modals.move.submit')" class='btn btn-primary float-right'/>
            </form>
          </div>
        </div>
        <div v-if="!unverifiedUser" class="operations">
          <a @click.prevent='shareRecord(record)' href='#' class="dropdown-item" v-if="record.shared">
            <img alt="Share" src="../assets/file-show/share.svg"/>
            {{ $t('records_list.dropdown.un_share') }}
          </a>
          <a @click.prevent='shareRecord(record)' href='#' class="dropdown-item" v-if="!record.shared">
            <img alt="Share" src="../assets/file-show/share.svg"/>
            {{ $t('records_list.dropdown.share') }}
          </a>
          <a :href="shareActions()" target="_blank" class="own-tooltip own-tooltip-left video-link"
             :data-tooltip="$t('record_show.click_to_watch_share')"><img src="../assets/video.svg"/></a>
        </div>
        <div class="operations">
          <a href="#" @click.prevent='openRecordModal($refs.deleteRecordModal)'>
            <img alt="Delete" src="../assets/file-show/delete.svg"/>
            <span>{{ $t('record_show.delete_file') }}</span>
          </a>
        </div>
        <h3>{{ $t('record_show.user') }}</h3>
        <div class="operations">
          <router-link :to="{ hash: '#events' }" @click.native=openRecordEvents()>
            <img alt="File activity" src="../assets/file-show/activity.png"/>
            <span>{{ $t('record_show.file_activity') }}</span>
          </router-link>
          <div class="set-data file-activity-data" v-if="showing == 'events'">
            <i class="fas fa-times" @click="hideIt"></i>
            <div class="empty-state" v-if='recordEvents.length == 0'>
              <p>{{ $t('record_show.no_activity') }}</p>
            </div>
            <div v-for='(value, key) in recordEvents' class="activity-group" :key="key">
              <p>{{ key }}</p>
              <ul v-for="data in value" class="activity" :key='data.id'>
                <li v-if="data.event === 'tagged'">
                  {{
                    $t('record.activity.tagged', {
                      tag: data.tag.name,
                      user: data.user.full_name,
                      date: formatDate(data.created_at)
                    })
                  }}
                </li>
                <li v-else-if="data.event === 'untagged'">
                  {{
                    $t('record.activity.untagged', {
                      tag: data.tag.name,
                      user: data.user.full_name,
                      date: formatDate(data.created_at)
                    })
                  }}
                </li>
                <li v-else-if="data.event === 'renamed'">
                  {{
                    $t('record.activity.renamed', {
                      old: data.from_name,
                      new: data.to_name,
                      user: data.user.full_name,
                      date: formatDate(data.created_at)
                    })
                  }}
                </li>
                <li v-else-if="data.event === 'moved'">
                  {{
                    $t('record.activity.moved', {
                      old: data.from_folder.name,
                      new: data.to_folder.name,
                      user: data.user.full_name,
                      date: formatDate(data.created_at)
                    })
                  }}
                </li>
                <li v-else-if="data.event === 'expiration_extended' && data.contact_id !== null">
                  {{
                    $t('record.activity.expiration_extended', {
                      user: data.user.full_name || data.user.email,
                      date: formatDate(data.created_at)
                    })
                  }}
                </li>
                <li v-else-if="data.event === 'expiration_extended_public' && data.contact_id !== null">
                  {{
                    $t('record.activity.expiration_extended_public', {
                      contact: data.contact.name,
                      date: formatDate(data.created_at)
                    })
                  }}
                </li>
                <li v-else-if="['checklists_moved', 'tags_moved', 'notes_moved', 'expiration_dates_moved',
                                'checklists_received', 'tags_received', 'notes_received', 'expiration_dates_received'].includes(data.event)
                               && data.user_id !== null">
                  {{
                    $t('record.activity.' + data.event, {
                      user: data.user.full_name,
                      target_filename: data.target_record.filename,
                      date: formatDate(data.created_at)
                    })
                  }}
                </li>
                <li v-else-if="['shared_tag_connected', 'shared_tag_disconnected'].includes(data.event) && data.tag_id !== null">
                  {{
                    $t('record.activity.' + data.event, {
                      user: data.user.full_name,
                      tag_name: data.tag.name,
                      date: formatDate(data.created_at)
                    })
                  }}
                </li>
                <li v-else>
                  {{
                    $t('record.activity.' + data.event, {user: data.user.full_name, date: formatDate(data.created_at)})
                  }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _modal from '../components/_modal'
import _tags from '../components/records/_tags'
import _user_image from '../components/_user_image'
import _preview from '../components/records/_preview'
import _expiration_form from '../components/records/_expiration_form'
import _version_options_form from '../components/records/_version_options_form'
import _dropzone from '../components/_dropzone'
import _checklist from '../components/records/_checklist'
import _upload_new_version_link from '../components/_upload_new_version_link'
import _request_new_version from '../components/_request_new_version'
import _tag_manager from '../components/tags/_tags_manager'
import _single_select from '../components/shared/_single_select'
import _infinite_single_select from '../components/shared/_infinite_single_select'
import _share_record_form from '../components/shared/records/_share_record_form'
import _record_subscription_modal from '../components/records/_subscription_modal'
import _multi_select from '../components/shared/_multi_select'
import TransitionExpand from '../components/shared/transition_expand'
import FileCommunication from '../components/communications/RecordMessage'
import _checklist_preview from '../components/records/_checklist_preview'

export default {
  components: {
    tags: _tags,
    modal: _modal,
    preview: _preview,
    dropzone: _dropzone,
    checklist: _checklist,
    'version-options-form': _version_options_form,
    'user-image': _user_image,
    'tag-manager': _tag_manager,
    'single-select': _single_select,
    'infinite-single-select': _infinite_single_select,
    'expiration-form': _expiration_form,
    'share-record-form': _share_record_form,
    'request-new-version': _request_new_version,
    'upload-new-version-link': _upload_new_version_link,
    'record-subscription-modal': _record_subscription_modal,
    'multi-select': _multi_select,
    'communication': FileCommunication,
    'transition-expand': TransitionExpand,
    'checklist-preview': _checklist_preview
  },
  props: {
    id: {
      required: true
    },
    hash: {},
    propRecords: {
      type: Array
    }
  },
  created() {
    this.store.getRecordNotes(this.$props.id)
    this.loadRecord()
    this.fetchTabData()

    this.store.sendMetrics({name: 'record', params: this.$route.params, path: this.$route.fullPath})

    if (this.activeTab)
      this.showing = this.activeTab
  },
  data() {
    return {
      modalVersion: {},
      emailAddresses: [],
      tagSearch: '',
      recordForVersion: {},
      folderToMove: {},
      folderToCopy: {},
      modalNote: {},
      showing: null,
      uploadedRecords: [],
      searchQuery: '',
      text: '',
      moveActionDisabled: false,
      mentionsFilter: [],
      replacedRecords: [],
      versionOptions: [],
      versionOptionsSave: true,
      versionOptionsDoNothing: false,
      isProcessing: false
    }
  },
  watch: {
    id() {
      this.store.getRecordNotes(this.$props.id)
      this.loadRecord()
    },
    activeTab() {
      this.fetchTabData()
    },
  },
  computed: {
    activeTab() {
      if (this.$props.hash)
        return this.$props.hash.substring(1)
    },
    records() {
      return this.recordsList.filter((record) => {
        //fix for change version job is slower than the api response
        return !this.replacedRecords.includes(record.id)
      })
    },
    currentUserVersionOptions() {
      // Fix checklists always enable
      const versionOpt = this.currentUser.version_options || (["checklists"])
      if (!versionOpt.includes("checklists")) {
        versionOpt.push("checklists");
      }
      return versionOpt || []
    },
    recordMessagesStats() {
      return this.stats.recordMessageStats || {}
    },
    repliesStats(){
      return this.recordMessagesStats.replies || []
    },
    newMessageStats(){
      return this.recordMessagesStats.new_messages || []
    },
    recordsList() {
      return this.$props.propRecords || this.lists.recordsForNavigation || []
    },
    recordsForNewVersion() {
      return this.lists.recordsForNewVersion || []
    },
    record() {
      return this.state.record || {}
    },
    modalRecord() {
      return Object.assign({}, this.record)
    },
    subscriptions() {
      return this.record.subscriptions || []
    },
    isSubscribed() {
      return this.subscriptions.some(s => s.user_id === this.currentUser.id)
    },
    recordTags() {
      return this.record.tags || []
    },
    folder() {
      return this.record.folder || {}
    },
    checklist() {
      return this.record.checklist_items || []
    },
    documentResponse() {
      return this.record.document_response || {}
    },
    ocrFilteredContent() {
      return this.record.ocr_content.filter(page => {
        return page.text.includes(this.searchQuery)
      })
    },
    contact() {
      if (this.folder.contact_id)
        return this.folder.contact || {}
    },
    recordIndex() {
      return this.records.findIndex(({id}) => id === this.record.id)
    },
    nextRecord() {
      if (this.recordIndex < 0 || this.records.length < 2)
        return

      return this.records[this.recordIndex + 1] || this.records[0]
    },
    prevRecord() {
      if (this.recordIndex < 0 || this.records.length < 2)
        return

      return this.records[this.recordIndex - 1] || this.records[this.records.length - 1]
    },
    recordEvents() {
      if (!this.state.recordEvents)
        return []

      return this.state.recordEvents
    },
    recordNotes() {
      if (!this.state.recordNotes)
        return []
      return this.state.recordNotes.slice().sort((a, b) => b.created_at.localeCompare(a.created_at))
    },
    recordVersions() {
      return this.state.recordVersions || []
    },
    ocrInfo() {
      return this.state.ocrInfo || {}
    },
    ocrFileLimit() {
      return this.ocrInfo.ocr_limit
    },
    isOcrEnabled() {
      return this.ocrFileLimit > 0
    },
    ocrFileCurrent() {
      return this.ocrInfo.ocr_current
    },
    ocrLimitReached() {
      return this.ocrFileCurrent === this.ocrFileLimit
    },
    mentions() {
      return this.lists.mentions || []
    },
    messages() {
      return this.state.recordMessages || []
    },
    addressBook() {
      return this.lists.addressBook || []
    },
    messageIsSelected() {
      return (Object.keys(this.selectedMessage).length > 0)
    },
    uploadedFrom() {
      switch (this.record.uploaded_from) {
        case 'app':
          return this.$t('record.through_app')
        case 'email':
          return this.$t('record.through_email')
        case 'public_page':
          return this.$t('record.through_public_page')
        case 'desktop':
          return this.$t('record.through_desktop')
        case 'api':
          return this.$t('record.through_api')
      }
    },
    currentOrgContactReminderDays() {
      return String(this.currentOrg.contact_reminder_days) || {}
    },
  },
  methods: {
    repliedVia(via) {
      switch (via) {
        case 'app':
          return this.$t('record_message.replied_from_app')
        case 'email':
          return this.$t('record_message.replied_from_email')
        case 'public_page':
          return this.$t('record_message.replied_from_public_page')
      }
    },
    getRecordMessages(options, save, doNothing) {
      this.versionOptions = options
      this.versionOptionsSave = save
      this.versionOptionsDoNothing = doNothing
    },
    versionOptionsFormCallback(options, save, doNothing) {
      this.versionOptions = options
      this.versionOptionsSave = save
      this.versionOptionsDoNothing = doNothing
    },
    updateChecklistItems(new_record_id) {
      const ch_items = this.record.checklist_items || []
      const new_ch_items = []
      ch_items.map((item) => {
        item.record_id = new_record_id
        new_ch_items.push(item)
      }, [])
      return new_ch_items || []
    },
    hideIt() {
      this.showing = null
    },
    hideChecklistPreview(){
      this.$refs.checklistPreview.close()
    },
    loadRecord() {
      this.store.getRecord(this.$props.id).then(() => {
        this.loadVersionOptions()
        this.loadRecordNotes()
        this.loadRecordsForNavigation()
      }).catch(() => {
        this.returnToFolder()
      }).finally(() => {
        this.loadStatsRecordMessage()
      })
    },
    loadStatsRecordMessage(){
      if(this.toggles('file_communication')){
        this.statStore.getCommunicationMessagesStats({ 'filters[record_id]': this.$props.id, 'filters[to_user_id]': this.currentUser.id })
      }
    },
    loadRecordsForNavigation() {
      this.listStore.getRecordsForNavigation(this.folder.id)
    },
    loadVersionOptions() {
      this.store.getCurrentUser().then((data) => {
        this.versionOptions = data.user.version_options || []
      })
    },
    loadRecordMessages() {
      this.listStore.getAddressBook()
    },
    loadRecordNotes() {
      this.store.getRecordNotes(this.$props.id)
    },
    fetchTabData() {
      switch (this.activeTab) {
        case 'events':
          this.store.getRecordEvents(this.$props.id)
          break
        case 'notes':
          this.loadRecordNotes()
          break
        case 'messages':
          this.loadRecordMessages()
          break
        case 'versions':
          this.store.getRecordVersions(this.$props.id)
          break
      }
    },
    openRecordModal(modal) {
      modal.open()
    },
    openRecordVersionModal(version, modal) {
      this.modalVersion = Object.assign({}, version)
      this.loadVersionOptions()
      modal.open()
    },
    openModal(modal) {
      modal.open()
    },
    openNoteModal(note, modal) {
      this.modalNote = Object.assign({}, note)
      modal.open()
    },
    renameFile(e) {
      this.updateRecord(e).then(() => {
        this.store.sendMetrics({name: 'rename_file', params: this.$route.params, path: this.$route.fullPath})
        this.showing = null
        this.loadRecord()
        this.fetchTabData()
      })
    },
    moveFile(e) {
      this.moveActionDisabled = true
      this.updateRecord(e).then(() => {
        this.store.sendMetrics({name: 'move_file', params: this.$route.params, path: this.$route.fullPath})
        this.showing = null
        this.loadRecord()
        this.fetchTabData()
        this.moveActionDisabled = false
      })
    },
    copyFile(e) {
      this.store.copyRecord(this.modalRecord, new FormData(e.target)).then(() => {
        this.store.sendMetrics({name: 'copy_file', params: this.$route.params, path: this.$route.fullPath})
        this.showing = null
        this.$toastr.success(this.$t('record.updated'))
        this.loadRecord()
        this.fetchTabData()
      })
    },
    deleteFile() {
      this.store.destroyRecord(this.modalRecord).then(() => {
        this.store.sendMetrics({name: 'remove_file', params: this.$route.params, path: this.$route.fullPath})
        this.$refs.deleteRecordModal.close()
        this.redirectBack()
      })
    },
    deleteVersion() {
      this.store.destroyRecord(this.modalVersion).then(() => {
        this.store.sendMetrics({name: 'remove_file', params: this.$route.params, path: this.$route.fullPath})
        this.$refs.deleteRecordVersionModal.close()
        this.fetchTabData()
      })
    },
    starRecord() {
      return this.store.updateRecord(this.record, {starred: !this.record.starred}).then(() => {
        this.store.sendMetrics({name: 'star_file', params: this.$route.params, path: this.$route.fullPath})
        this.loadRecord()
        this.fetchTabData()
      })
    },
    createOcrFromRecord() {
      this.store.createRecordOcr(this.record.id).then(() => {
        this.store.sendMetrics({name: 'create_orc_from_file', params: this.$route.params, path: this.$route.fullPath})
        this.loadRecord()
        this.fetchTabData()
        this.$refs.extractText.close()
        this.$toastr.success(this.$t('record.updated'))
      })
    },
    createNote(e) {
      this.store.createRecordNote(this.record.id, new FormData(e.target)).then(() => {
        this.store.sendMetrics({name: 'create_note', params: this.$route.params, path: this.$route.fullPath})
        this.loadRecord()
        this.fetchTabData()
        e.target.reset()
      })
    },
    editNote(e) {
      this.store.editRecordNote(this.record.id, this.modalNote.id, new FormData(e.target)).then(() => {
        this.store.sendMetrics({name: 'edit_note', params: this.$route.params, path: this.$route.fullPath})
        this.loadRecord()
        this.fetchTabData()
        this.$refs.editNoteModal.close()
      })
    },
    deleteNote() {
      this.store.destroyRecordNote(this.record.id, this.modalNote.id).then(() => {
        this.store.sendMetrics({name: 'remove_note', params: this.$route.params, path: this.$route.fullPath})
        this.loadRecord()
        this.fetchTabData()
        this.$refs.deleteNoteModal.close()
      })
    },
    updateRecord(e) {
      return this.store.updateRecord(this.record, new FormData(e.target)).then(() => {
        this.showing = null
        this.loadRecord()
        this.fetchTabData()
        this.$toastr.success(this.$t('record.updated'))
      })
    },
    updateExpiration(e) {
      return this.store.updateRecord(this.record, new FormData(e.target)).then(() => {
        this.showing = null
        this.loadRecord()
        this.fetchTabData()
        this.$toastr.success(this.$t('record.updated'))
        this.store.sendMetrics({name: 'update_expiration', params: this.$route.params, path: this.$route.fullPath})
      })
    },
    uppySuccessCallback(file, res) {
      let params = Object.assign({}, {
        folder_id: this.record.folder_id,
        filename: file.name,
        mime_type: file.type,
        size: file.size,
        location: res.uploadURL,
        inside_app: true
      })

      return this.store.replaceRecord(this.record, params).then((data) => {
        this.moveOldVersionData(data.id, this.id)

        this.$refs.uploadVersionModal.close()
        this.$refs.dropzone.reset()
        this.uploadedRecords.push(data.id)
        this.replacedRecords.push(this.id)

        this.$router.replace({name: 'record', params: {id: data.id}})
      })
    },
    uppyCompleteCallback(data) {
      let records = data.successful
      if (records.length > 1) {
        this.store.createUploadFolderActivity(this.record.folder_id, {
          event: 'upload_file',
          count: records.length,
          user_id: this.currentUser.id
        })
      } else if (records.length === 1) {
        let record = this.uploadedRecords[0]
        this.store.createUploadFolderActivity(this.record.folder_id, {
          event: 'upload_file',
          record_id: record.id,
          user_id: this.currentUser.id
        })
      }
      this.$refs.uploadVersionModal.close()
    },
    moveOldVersionData(new_record_id, old_record_id) {
      let params = this.versionOptions
      let updateUserPromise = null
      let moveOldDataPromise = null

      if (this.versionOptionsSave)
        updateUserPromise = this.store.updateUserProfile({user: {version_options: params}})

      if (!this.versionOptionsDoNothing && this.versionOptions.length > 0)
        moveOldDataPromise = this.store.updateNewVersionOptions(new_record_id, old_record_id, params)

      Promise.allSettled([updateUserPromise, moveOldDataPromise]).then(() => {
        this.store.sendMetrics({name: 'version_options_move', params: this.$route.params, path: this.$route.fullPath})
        this.loadRecord()
        this.fetchTabData()
      })
    },
    removeConnectedTag(tag) {
      this.store.removeConnectedTag(tag.id, {record_id: this.$route.params.id}).then(() => {
        this.loadRecord()
        this.fetchTabData()
      })
    },
    shareRecord(e) {
      let params = {}
      params.record = {shared: !this.record.shared}
      return this.store.updateRecord(this.record, params).then(() => {
        this.store.sendMetrics({name: 'share_file', params: this.$route.params, path: this.$route.fullPath})
        this.loadRecord()
        this.fetchTabData()
        this.$toastr.success(this.$t('record.updated'))
      })
    },
    openModalForRecords(modal) {
      const version = 1
      this.listStore.getRecordsForVersion(this.folder, this.record, version)
      modal.open()
    },
    makeRecordCurrent() {
      if (this.recordForVersion) {
        this.isProcessing = true

        this.store.replaceWithExistingRecord(this.$props.id, this.recordForVersion).then((data) => {
          this.moveOldVersionData(data.id, this.$props.id)
          this.store.sendMetrics({name: 'make_file_current', params: this.$route.params, path: this.$route.fullPath})
          this.recordForVersion = null
          this.$router.replace({name: 'record', params: {id: data.id, propRecords: this.records}})
          this.$toastr.success(this.$t('record.updated'))
          this.$refs.selectFromRelaticoFolder.close()
          this.isProcessing = false
        }).catch(() => {
          this.isProcessing = false
        })
      }
    },
    openNotes() {
      this.listStore.getMentions()
      this.showing = 'notes'
      this.store.sendMetrics({name: 'notes', params: this.$route.params, path: this.$route.fullPath})
    },
    openRecordMessages() {
      this.showing = 'messages'
      this.$refs.communication.openMessages()
    },
    openVersions() {
      this.showing = 'versions'
      this.store.sendMetrics({name: 'versions', params: this.$route.params, path: this.$route.fullPath})
    },
    openChecklistPreview() {
      this.checklist_preview = true
      this.store.sendMetrics({name: 'checklist_preview', params: this.$route.params, path: this.$route.fullPath})
    },
    openTags() {
      this.showing = 'tags'
      this.store.sendMetrics({name: 'tags', params: this.$route.params, path: this.$route.fullPath})
    },
    openRecordEvents() {
      this.showing = 'events'
      this.store.sendMetrics({name: 'events', params: this.$route.params, path: this.$route.fullPath})
    },
    userSelected(event) {
      if (event && event.detail && event.detail.item) {
        this.emailAddresses.push(event.detail.item.original.key)
        this.emailAddresses = this.emailAddresses.splice(0, this.emailAddresses.length, ...(new Set(this.emailAddresses)))
      }
    },
    returnToFolder() {
      if (this.folder.primary === 'root')
        this.$router.push({name: 'folders', params: {parent_id: 'root'}})
      else
        this.$router.push({name: 'folder', params: {id: this.folder.id}})
    },
    fileActions() {
      if (this.locale === 'de')
        return 'https://relatico.wistia.com/medias/wlqfj8b60y'
      return 'https://relatico.wistia.com/medias/fm6tkgh1oq'
    },
    checklistActions() {
      if (this.locale === 'de')
        return 'https://relatico.wistia.com/medias/fvxhzsxktk'
      return 'https://relatico.wistia.com/medias/j5p9xhtphg'
    },
    fileVersionsActions() {
      if (this.locale === 'de')
        return 'https://relatico.wistia.com/medias/05fnbbc2mw'
      return 'https://relatico.wistia.com/medias/l5urgbmk67'
    },
    textExtractActions() {
      if (this.locale === 'de')
        return 'https://relatico.wistia.com/medias/jep4s2g3rx'
      return 'https://relatico.wistia.com/medias/meifve6f6v'
    },
    shareActions() {
      if (this.locale === 'de')
        return 'https://relatico.wistia.com/medias/17kj9oojcp'
      return 'https://relatico.wistia.com/medias/85tb5xye4x'
    },
    getFolders(params) {
      return this.listStore.getFolders(params)
    },
  }
}
</script>

<style lang="sass" scoped>
@import "../styles/records"

.p-b-20
  padding-bottom: 20px

.no-checklist
  font-size: 16px
  padding: 20px 50px

.video-link
  margin-left: 10px
  font-size: 12px

.file-source
  div
    font-size: 16px

    a
      margin-left: 10px
      text-decoration: underline

  span
    color: #51BE8F

.versions table
  a
    color: #007bff

.back-button
  margin-bottom: 0
  cursor: pointer
  display: initial

  &:hover
    text-decoration: underline

.contact-image-wrapper
  width: 45px
  height: 45px
  line-height: 45px
  background: #56DAE2
  font-weight: bold
  text-align: center
  overflow: hidden
  display: block
  float: left
  color: #fff
  margin-right: 7px

  img
    vertical-align: top
    min-width: 100%
    min-height: 100%
    width: auto
    height: auto

  div
    border-radius: 50%

  &.org-pic
    background: #eee
    color: #000
</style>
